import { ReportPdfData } from '../types';
import { createEyebrow, createFooter } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the architecture nodes page for the PDF report
 * @param data Report data
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @param pageNumber Current page number
 * @param totalPages Total number of pages
 * @returns Architecture nodes page content for pdfmake
 */
export const createArchitectureNodesPage = (
  data: ReportPdfData,
  assets: PdfAssets | undefined,
  colors: Record<string, string>,
  pageNumber: number,
  totalPages: number
) => {
  // Define all architecture node types
  const allArchitectureTypes = ['cluster', 'sync-injector', 'load-balancer'];
  
  // Ensure selectedSections is an array
  const selectedSections = Array.isArray(data.selectedSections) ? data.selectedSections : [];
  
  // If ARCHITECTURE is selected, include all architecture types
  let architectureTypes = [];
  if (selectedSections.includes('ARCHITECTURE')) {
    architectureTypes = [...allArchitectureTypes];
  } else {
    // Otherwise, filter based on specific selections
    architectureTypes = allArchitectureTypes.filter(type => 
      selectedSections.includes(`ARCHITECTURE:${type}`) || 
      selectedSections.includes(type)
    );
  }
  
  // Check if there are any architecture nodes
  if (!data.architecture?.nodes) {
    return [];
  }
  
  const architectureNodes = data.architecture.nodes.filter(node => 
    architectureTypes.includes(node.type)
  );
  
  if (architectureNodes.length === 0) {
    return [];
  }
  
  // Group nodes by type
  const nodesByType: Record<string, any[]> = {};
  
  architectureNodes.forEach(node => {
    if (!nodesByType[node.type]) {
      nodesByType[node.type] = [];
    }
    nodesByType[node.type].push(node);
  });
  
  return [
    // Eyebrow section
    ...createEyebrow('Architecture Breakdown - Infrastructure', assets?.markUrl, colors),
    
    // Content section
    {
      stack: Object.entries(nodesByType).map(([type, nodes]) => {
        // Get a friendly name for the architecture type
        let typeName = 'Component';
        let typeDescription = '';
        
        switch (type) {
          case 'cluster':
            typeName = 'MetaRouter Cluster';
            typeDescription = 'Clusters are central components in your data pipeline that process incoming data before forwarding it to destinations. They can be deployed in different environments and configured with specialized modules to perform various data operations.';
            break;
          case 'sync-injector':
            typeName = 'Sync Injector™️';
            typeDescription = 'MetaRouter Sync Injector enriches your event stream with vendor-specific identifiers, enabling precise advertising retargeting, conversion measurement, and attribution.';
            break;
          case 'load-balancer':
            typeName = 'Load Balancer Node';
            typeDescription = 'A Load Balancer Node distributes incoming data traffic across multiple MetaRouter Clusters to ensure optimal performance and prevent any single server from becoming overwhelmed.';
            break;
        }
        
        return {
          columns: [
            {
              stack: [
                {
                  text: typeName,
                  fontSize: 14,
                  bold: true,
                  color: colors.black,
                  margin: [0, 0, 0, 8]
                },
                {
                  text: typeDescription,
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 0]
                }
              ],
              width: '60%',
              margin: [0, 0, 12, 0]
            },
                {
                  stack: nodes.map(node => {
                    // Get all node details
                    const nodeDetails = [];
                    
                    // Node name
                    nodeDetails.push({
                      text: node.label,
                      fontSize: 9,
                      bold: true,
                      color: colors.black,
                      margin: [0, 0, 0, 8]
                    });
                    
                    // Add node-specific fields based on type
                    if (type === 'cluster') {
                      // Cluster-specific fields
                      if (node.environment) {
                        nodeDetails.push({
                          text: `Environment: ${node.environment}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 0, 0, 2]
                        });
                      }
                      
                      if (node.modules && node.modules.length > 0) {
                        nodeDetails.push({
                          text: 'Enabled Modules:',
                          fontSize: 8,
                          bold: true,
                          color: colors.darkGrey,
                          margin: [0, 4, 0, 2]
                        });
                        
                        node.modules.forEach((module: any) => {
                          if (module.enabled) {
                            nodeDetails.push({
                              text: `• ${module.name}`,
                              fontSize: 8,
                              color: colors.darkGrey,
                              margin: [4, 0, 0, 2]
                            });
                          }
                        });
                      }
                      
                      if (node.teamAccess) {
                        nodeDetails.push({
                          text: `Team Access: ${node.teamAccess}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 4, 0, 2]
                        });
                      }
                      
                      if (node.customLabel) {
                        nodeDetails.push({
                          text: `Custom Label: ${node.customLabel}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 0, 0, 2]
                        });
                      }
                    } else if (type === 'sync-injector') {
                      // Sync Injector specific fields
                      if (node.domains && node.domains.length > 0) {
                        nodeDetails.push({
                          text: 'Domains:',
                          fontSize: 8,
                          bold: true,
                          color: colors.darkGrey,
                          margin: [0, 4, 0, 2]
                        });
                        
                        node.domains.forEach((domain: string) => {
                          nodeDetails.push({
                            text: `• ${domain}`,
                            fontSize: 8,
                            color: colors.darkGrey,
                            margin: [4, 0, 0, 2]
                          });
                        });
                      }
                      
                      if (node.crossDomain !== undefined) {
                        nodeDetails.push({
                          text: `Cross Domain: ${node.crossDomain ? 'Enabled' : 'Disabled'}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 4, 0, 2]
                        });
                      }
                      
                      if (node.idSyncs && node.idSyncs.length > 0) {
                        nodeDetails.push({
                          text: 'ID Syncs:',
                          fontSize: 8,
                          bold: true,
                          color: colors.darkGrey,
                          margin: [0, 4, 0, 2]
                        });
                        
                        node.idSyncs.forEach((idSync: string) => {
                          nodeDetails.push({
                            text: `• ${idSync}`,
                            fontSize: 8,
                            color: colors.darkGrey,
                            margin: [4, 0, 0, 2]
                          });
                        });
                      }
                    } else if (type === 'load-balancer') {
                      // Load Balancer specific fields
                      if (node.strategy) {
                        nodeDetails.push({
                          text: `Strategy: ${node.strategy}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 4, 0, 2]
                        });
                      }
                      
                      if (node.customLabel) {
                        nodeDetails.push({
                          text: `Custom Label: ${node.customLabel}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 0, 0, 2]
                        });
                      }
                      
                      if (node.status) {
                        nodeDetails.push({
                          text: `Status: ${node.status}`,
                          fontSize: 8,
                          color: colors.darkGrey,
                          margin: [0, 0, 0, 2]
                        });
                      }
                    }
                    
                    // Add bottom margin to separate nodes
                    nodeDetails.push({
                      text: '',
                      margin: [0, 0, 0, 16]
                    });
                    
                    return {
                      stack: nodeDetails
                    };
                  }),
                  width: '40%',
                  margin: [12, 0, 0, 0]
                }
          ],
          margin: [64, 48, 40, 32]
        };
      }),
    },
    
    // Footer
    createFooter(pageNumber, totalPages, colors),
    { text: '', pageBreak: 'after' }
  ];
};
